import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Plus, Settings, Download, Package } from 'lucide-react';
import { useMutation } from '@apollo/client';
import { UPDATE_ITEM } from '../graphql/mutations';
import imageCompression from 'browser-image-compression';
import DigitalLinkForm from './DigitalLinkForm';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { useDigitalAssetCleanup } from '../hooks/useDigitalAssetCleanup';



const EditItemPopup = ({ isOpen, onClose, onSubmit, item, categories, catalogId }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [formattedPrice, setFormattedPrice] = useState('');
    const [category, setCategory] = useState('');
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [scheduledStart, setScheduledStart] = useState('');
    const [scheduledEnd, setScheduledEnd] = useState('');
    const [repeatType, setRepeatType] = useState('');
    const [repeatTime, setRepeatTime] = useState('');
    const [repeatDay, setRepeatDay] = useState('');
    const [repeatDate, setRepeatDate] = useState('');
    const [isCompressing, setIsCompressing] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [newCategory, setNewCategory] = useState('');
    const [showProductTypeWarning, setShowProductTypeWarning] = useState(false);

    // Digital product states
    const [productType, setProductType] = useState('PHYSICAL');
    const [digitalSettings, setDigitalSettings] = useState({
        downloadLimit: -1,
        linkExpiryHours: -1,
        allowPreview: false,
        previewDuration: 30
    });
    const [showDigitalUpload, setShowDigitalUpload] = useState(false);
    const [digitalAssetType, setDigitalAssetType] = useState('files');
    const [pendingDigitalLinks, setPendingDigitalLinks] = useState([]);

    // New states for file upload
    const [pendingDigitalAssets, setPendingDigitalAssets] = useState([]);
    const [uploadingAssets, setUploadingAssets] = useState(false);

    const { trackUpload, cleanupTempFiles, clearTracking } = useDigitalAssetCleanup();


    const [updateItem] = useMutation(UPDATE_ITEM);

    const {
        checkDigitalFileSize,
        checkDigitalFileCount,
        checkTotalDigitalSize,
        limits
    } = usePlanLimits(catalogId);

    useEffect(() => {
        if (item) {
            setName(item.name || '');
            setDescription(item.description || '');
            const initialPrice = item.priceVariants[0]?.price.toString() || '';
            setPrice(initialPrice);
            setFormattedPrice(formatPrice(initialPrice));
            if (item.category) {
                const categoryValue = typeof item.category === 'object' ? item.category.id : item.category;
                setCategory(categoryValue);
            } else {
                setCategory('');
            }
            setImagePreview(item.image || null);
            setScheduledStart(item.scheduledStart || '');
            setScheduledEnd(item.scheduledEnd || '');
            setRepeatType(item.repeatAvailability?.[0]?.type || '');
            setRepeatTime(item.repeatAvailability?.[0]?.time || '');
            setRepeatDay(item.repeatAvailability?.[0]?.day || '');
            setRepeatDate(item.repeatAvailability?.[0]?.date || '');
            setPreviewUrl(item.image || '');

            setProductType(item.productType || 'PHYSICAL');
            setShowDigitalUpload(item.productType !== 'PHYSICAL');

            const hasFiles = item.digitalAssets?.some(a => a.assetType === 'FILE') || false;
            const hasLinks = item.digitalAssets?.some(a => a.assetType === 'URL') || false;

            if (hasFiles && !hasLinks) {
                setDigitalAssetType('files');
            } else if (hasLinks && !hasFiles) {
                setDigitalAssetType('links');
            } else if (hasFiles && hasLinks) {
                setDigitalAssetType('files');
            }

            if (item.digitalSettings) {
                setDigitalSettings({
                    downloadLimit: item.digitalSettings.downloadLimit ?? 5,
                    linkExpiryHours: item.digitalSettings.linkExpiryHours ?? 48,
                    allowPreview: item.digitalSettings.allowPreview ?? false,
                    previewDuration: item.digitalSettings.previewDuration ?? 30
                });
            }
        }
    }, [item]);

    const formatPrice = (value) => {
        const numericValue = value.replace(/[^\d]/g, '');
        return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const handlePriceChange = (e) => {
        const rawValue = e.target.value;
        const formattedValue = formatPrice(rawValue);
        setFormattedPrice(formattedValue);
        setPrice(rawValue.replace(/,/g, ''));
    };

    const handleCancel = async () => {
        const keysToCleanup = pendingDigitalAssets
            .filter(a => a.uploadMethod === 'direct' && a.filename)
            .map(a => a.filename);

        if (keysToCleanup.length > 0) {
            await cleanupTempFiles(keysToCleanup);
        }

        resetForm();
        onClose();
    };


    const handleProductTypeChange = (newType) => {
        const currentType = item.productType || 'PHYSICAL';

        if ((currentType === 'DIGITAL' || currentType === 'HYBRID') &&
            newType === 'PHYSICAL' &&
            item.digitalAssets && item.digitalAssets.length > 0) {
            setShowProductTypeWarning(true);
        } else {
            setProductType(newType);
            setShowDigitalUpload(newType !== 'PHYSICAL');
        }
    };

    const confirmProductTypeChange = (newType) => {
        setProductType(newType);
        setShowDigitalUpload(newType !== 'PHYSICAL');
        setShowProductTypeWarning(false);
    };

    const handleDigitalSettingsChange = (setting, value) => {
        setDigitalSettings(prev => ({
            ...prev,
            [setting]: value
        }));
    };

    // Upload method for large files (same as AddItemPopup)
    const uploadLargeFile = async (file, onProgress) => {
        try {
            const presignResponse = await fetch(
                `${import.meta.env.VITE_BACKEND_URL}/api/upload/presigned`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                    },
                    body: JSON.stringify({
                        filename: file.name,
                        fileType: file.type,
                        fileSize: file.size
                    })
                }
            );

            if (!presignResponse.ok) {
                const error = await presignResponse.json();
                throw new Error(error.error || 'Failed to get upload URL');
            }

            const { uploadUrl, publicUrl, key } = await presignResponse.json();

            // Track the upload IMMEDIATELY
            trackUpload(key);

            return await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                xhr.upload.addEventListener('progress', (e) => {
                    if (e.lengthComputable && onProgress) {
                        const percentComplete = (e.loaded / e.total) * 100;
                        onProgress(percentComplete, e.loaded, e.total);
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status === 200) {
                        resolve({
                            url: publicUrl,
                            publicId: key,
                            originalName: file.name,
                            fileSize: file.size,
                            fileType: file.type
                        });
                    } else {
                        reject(new Error(`Upload failed with status ${xhr.status}`));
                    }
                });

                xhr.addEventListener('error', () => {
                    // Don't remove from tracking - let cleanup handle it
                    reject(new Error('Network error during upload'));
                });

                xhr.addEventListener('abort', () => {
                    // Don't remove from tracking - let cleanup handle it
                    reject(new Error('Upload aborted'));
                });

                xhr.open('PUT', uploadUrl);
                xhr.setRequestHeader('Content-Type', file.type);
                xhr.send(file);
            });
        } catch (error) {
            console.error('Upload error:', error);
            throw error;
        }
    };

    const handleRemovePendingAsset = async (assetId) => {
        const assetToRemove = pendingDigitalAssets.find(a => a.id === assetId);

        // Cleanup uploaded file if it was direct upload
        if (assetToRemove?.uploadMethod === 'direct' && assetToRemove?.filename) {
            await cleanupTempFiles([assetToRemove.filename]);
        }

        setPendingDigitalAssets(prev => prev.filter(asset => asset.id !== assetId));
    };


    const handleDigitalAssetAdd = async (files) => {
        if (!files?.length) return;

        // Get current total including existing assets
        const existingAssetCount = item?.digitalAssets?.length || 0;
        const currentPendingCount = pendingDigitalAssets.length;
        const totalCurrentCount = existingAssetCount + currentPendingCount;

        const countCheck = checkDigitalFileCount(totalCurrentCount, files.length);
        if (!countCheck.canAdd) {
            setErrorMessage(countCheck.message);
            return;
        }

        setUploadingAssets(true);
        setErrorMessage('');

        try {
            const newAssets = [];

            // Calculate current total size including existing assets
            const existingTotalSize = item?.digitalAssets?.reduce((sum, a) => sum + (a.fileSize || 0), 0) || 0;
            const pendingTotalSize = pendingDigitalAssets.reduce((sum, a) => sum + a.fileSize, 0);
            let accumulatedSize = existingTotalSize + pendingTotalSize; // CHANGE: rename from currentTotalSize

            for (const file of files) {
                const sizeCheck = checkDigitalFileSize(file.size);
                if (!sizeCheck.canAdd) {
                    setErrorMessage(`${file.name}: ${sizeCheck.message}`);
                    continue;
                }

                const totalCheck = checkTotalDigitalSize(
                    accumulatedSize, // CHANGE: use accumulatedSize
                    file.size,
                    [
                        ...(item?.digitalAssets?.map(a => a.fileSize || 0) || []),
                        ...pendingDigitalAssets.map(a => a.fileSize)
                    ]
                );
                if (!totalCheck.canAdd) {
                    setErrorMessage(totalCheck.message);
                    break;
                }

                let asset;

                try {
                    if (file.size <= 5 * 1024 * 1024) {
                        const base64 = await new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => resolve(reader.result.split(',')[1]);
                            reader.onerror = reject;
                            reader.readAsDataURL(file);
                        });

                        asset = {
                            id: Date.now() + Math.random(),
                            filename: file.name,
                            originalName: file.name,
                            fileSize: file.size,
                            fileType: file.type,
                            base64Data: base64,
                            isTemp: true,
                            uploadMethod: 'base64'
                        };
                    } else {
                        console.log('Uploading large file directly:', file.name);
                        const uploadResult = await uploadLargeFile(file);

                        asset = {
                            id: Date.now() + Math.random(),
                            filename: uploadResult.publicId,
                            originalName: uploadResult.originalName,
                            fileSize: uploadResult.fileSize,
                            fileType: uploadResult.fileType,
                            storageUrl: uploadResult.url,
                            isTemp: true,
                            uploadMethod: 'direct'
                        };
                    }

                    newAssets.push(asset);
                    accumulatedSize += file.size; // CHANGE: use accumulatedSize
                    console.log('Asset prepared successfully:', file.name, asset.uploadMethod);
                } catch (fileError) {
                    console.error('Error processing file:', file.name, fileError);
                    setErrorMessage(`Failed to process ${file.name}: ${fileError.message}`);
                }
            }

            if (newAssets.length > 0) {
                setPendingDigitalAssets(prev => [...prev, ...newAssets]);
            }
        } catch (error) {
            console.error('Error preparing digital assets:', error);
            setErrorMessage(error.message);
        } finally {
            setUploadingAssets(false);
        }
    };


    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleImageChange = async (e) => {
        setIsCompressing(true);
        setErrorMessage('');

        const file = e.target.files[0];

        if (file) {
            try {
                if (file.size > 100 * 1024 * 1024) {
                    throw new Error('File size exceeds 100MB. Please choose a smaller image.');
                }

                let processedFile = file;

                if (file.type === 'image/heic' || file.type === 'image/heif') {
                    try {
                        const jpegBlob = await heic2any({
                            blob: file,
                            toType: 'image/jpeg',
                            quality: 0.8
                        });
                        processedFile = new File([jpegBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
                            type: 'image/jpeg'
                        });
                    } catch (heicError) {
                        throw new Error('Unable to convert HEIC image. Please try a different format.');
                    }
                }

                if (!processedFile.type.startsWith('image/')) {
                    throw new Error('Invalid file type. Please upload an image file.');
                }

                const options = {
                    maxSizeMB: 4,
                    maxWidthOrHeight: 1600,
                    useWebWorker: true,
                };

                const compressedFile = await imageCompression(file, options);

                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64String = reader.result.split(',')[1];
                    setImage(base64String);
                    setPreviewUrl(reader.result);
                    setIsCompressing(false);
                };
                reader.onerror = (error) => {
                    throw new Error('Error reading file. Please try again.');
                };
                reader.readAsDataURL(compressedFile);
            } catch (error) {
                console.error('Error processing image:', error);
                setErrorMessage(error.message || 'An error occurred while processing the image. Please try again.');
                setIsCompressing(false);
                setImage(null);
                setPreviewUrl('');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsUpdating(true);
        try {
            const updateData = {
                id: item.id,
                name,
                description,
                priceVariants: [{ variant: 'default', price: parseFloat(price) }],
                catalog: catalogId,
                category: category === '' || category === 'new' ? null : category,
                image,
                scheduledStart,
                scheduledEnd,
                repeatAvailability: repeatType ? [{
                    type: repeatType,
                    time: repeatTime,
                    day: repeatDay,
                    date: repeatDate
                }] : null,
                isVisible: item.isVisible,
                productType,
                requiresShipping: productType !== 'DIGITAL',
                digitalSettings: (productType === 'DIGITAL' || productType === 'HYBRID') ? digitalSettings : null,

                // Add pending digital assets
                ...(pendingDigitalAssets.length > 0 && {
                    pendingDigitalAssets: pendingDigitalAssets.map(asset => ({
                        filename: asset.filename,
                        originalName: asset.originalName,
                        fileSize: asset.fileSize,
                        fileType: asset.fileType,
                        isTemp: asset.isTemp,
                        uploadMethod: asset.uploadMethod,
                        assetType: 'FILE',
                        ...(asset.uploadMethod === 'base64' && { base64Data: asset.base64Data }),
                        ...(asset.uploadMethod === 'direct' && { storageUrl: asset.storageUrl })
                    }))
                }),

                // Add digital links
                ...(pendingDigitalLinks.length > 0 && {
                    digitalLinks: pendingDigitalLinks.map(link => ({
                        linkUrl: link.linkUrl,
                        linkType: link.linkType,
                        linkDescription: link.linkDescription,
                        originalName: link.originalName || `${link.linkType.toLowerCase()}_link`
                    }))
                })
            };

            await onSubmit(updateData);
            clearTracking();
            resetForm();
            onClose();
        } catch (error) {
            console.error('Error updating item:', error);
            onSubmit(null, error);
        } finally {
            setIsUpdating(false);
        }
    };

    const resetForm = () => {
        setPendingDigitalAssets([]);
        setPendingDigitalLinks([]);
        setUploadingAssets(false);
        setErrorMessage('');
    };

    // Get current assets based on selected type
    const currentAssets = item.digitalAssets?.filter(a =>
        digitalAssetType === 'files' ? a.assetType === 'FILE' : a.assetType === 'URL'
    ) || [];

    if (!isOpen) return null;

    const existingAssetCount = item?.digitalAssets?.length || 0;
    const existingLinkCount = item?.digitalLinks?.length || 0;
    const currentFileCount = existingAssetCount + existingLinkCount + pendingDigitalAssets.length + pendingDigitalLinks.length;

    const existingTotalSize = item?.digitalAssets?.reduce((sum, a) => sum + (a.fileSize || 0), 0) || 0;
    const pendingTotalSize = pendingDigitalAssets.reduce((sum, a) => sum + a.fileSize, 0);
    const currentTotalSize = existingTotalSize + pendingTotalSize;

    const maxFiles = limits?.digitalFiles?.maxFilesPerProduct || 5;
    const maxFileSize = limits?.digitalFiles?.maxFileSize || 100 * 1024 * 1024;
    const maxTotalSize = limits?.digitalFiles?.maxTotalSizePerProduct || 500 * 1024 * 1024;


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h4 className="text-xl font-bold text-[#191970]">Edit Item</h4>
                    <button onClick={handleCancel} className="text-gray-500 bg-gray-100 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-500">Name *</label>
                        <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className="mt-1 px-2 py-2 block w-full rounded-md bg-white border-[1.5px] border-gray-200 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-10"
                        />
                    </div>

                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-500">Description <span className='text-[70%]'>(optional)</span></label>
                        <textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="mt-1 px-2 py-2 block w-full rounded-md bg-white border-[1.5px] border-gray-200 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-20"
                            rows="3"
                        ></textarea>
                    </div>

                    <div>
                        <label htmlFor="price" className="block text-sm font-medium text-gray-500">Price *</label>
                        <div className='flex flex-row'>
                            <p className='mt-4 mr-2'>₦</p>
                            <input
                                type="text"
                                id="price"
                                value={formattedPrice}
                                onChange={handlePriceChange}
                                required
                                className="mt-1 px-2 py-2 block w-full rounded-md bg-white border-[1.5px] border-gray-200 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-10"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-500">Category <span className='text-[70%]'>(optional)</span></label>
                        <select
                            id="category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="mt-1 px-2 py-2 block w-full rounded-md bg-white border-[1.5px] border-gray-200 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50"
                        >
                            <option value="">No Category</option>
                            {categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Product Type Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Product Type
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {[
                                { value: 'PHYSICAL', label: 'Physical', icon: '📦' },
                                { value: 'DIGITAL', label: 'Digital', icon: '💾' },
                            ].map((type) => (
                                <button
                                    key={type.value}
                                    type="button"
                                    onClick={() => handleProductTypeChange(type.value)}
                                    className={`p-3 bg-white rounded-lg border-2 transition-all duration-200 ${productType === type.value
                                        ? 'border-[#191970] bg-[#191970]/5 text-[#191970]'
                                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                        }`}
                                >
                                    <div className="text-center">
                                        <div className="text-lg mb-1">{type.icon}</div>
                                        <div className="text-xs sm:text-sm font-medium">{type.label}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Product Type Change Warning Modal */}
                    {showProductTypeWarning && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
                            <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-sm sm:max-w-md mx-4">
                                <div className="flex items-start mb-4">
                                    <span className="text-yellow-400 text-xl sm:text-2xl mr-3 flex-shrink-0">⚠️</span>
                                    <div>
                                        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                                            Change Product Type?
                                        </h3>
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                                            <p className="text-sm text-yellow-800">
                                                Changing to "Physical Product" will remove all digital files and disable customer downloads.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
                                    <button
                                        onClick={() => setShowProductTypeWarning(false)}
                                        className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-800 text-sm rounded-lg hover:bg-gray-300 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => confirmProductTypeChange('PHYSICAL')}
                                        className="w-full sm:w-auto px-4 py-2 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 transition-colors"
                                    >
                                        Continue
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Digital Product Section */}
                    {showDigitalUpload && (
                        <div className="bg-blue-50 rounded-lg p-4 space-y-4">
                            <div className="flex items-center justify-between"> {/* CHANGE from space-x-2 */}
                                <div className="flex items-center space-x-2">
                                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-blue-600 text-xs">💾</span>
                                    </div>
                                    <h3 className="text-sm font-medium text-blue-900">Digital Content</h3>
                                </div>
                                {/* ADD: Show usage indicator */}
                                <span className="text-xs text-gray-600">
                                    {currentFileCount}/{maxFiles} files
                                </span>
                            </div>

                            {/* Show existing digital assets */}
                            {item?.digitalAssets && item.digitalAssets.length > 0 && (
                                <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-gray-700">Current Files:</h4>
                                    <div className="space-y-2">
                                        {item.digitalAssets.map((asset) => (
                                            <div key={asset._id} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                                                <div className="flex items-center space-x-2 min-w-0">
                                                    <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                                                        <Package className="w-3 h-3 text-blue-600" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">{asset.originalName}</p>
                                                        <p className="text-xs text-gray-500">{formatFileSize(asset.fileSize)}</p>
                                                    </div>
                                                </div>
                                                <span className="text-xs text-green-600 ml-2">Uploaded</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Asset Type Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Add more content:
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setDigitalAssetType('files');
                                            setPendingDigitalLinks([]);
                                        }}
                                        className={`p-3 bg-transparent rounded-lg border-2 transition-all ${digitalAssetType === 'files'
                                            ? 'border-[#191970] bg-[#191970]/5 text-[#191970]'
                                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                            }`}
                                    >
                                        <div className="text-center">
                                            <div className="text-lg mb-1">📁</div>
                                            <div className="text-xs font-medium">Upload Files</div>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setDigitalAssetType('links');
                                            setPendingDigitalAssets([]);
                                        }}
                                        className={`p-3 bg-transparent rounded-lg border-2 transition-all ${digitalAssetType === 'links'
                                            ? 'border-[#191970] bg-[#191970]/5 text-[#191970]'
                                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                            }`}
                                    >
                                        <div className="text-center">
                                            <div className="text-lg mb-1">🔗</div>
                                            <div className="text-xs font-medium">Add Links</div>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            {/* File Upload Section */}
                            {digitalAssetType === 'files' && (
                                <div className="space-y-3">
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                        <input
                                            type="file"
                                            id="digital-files-edit"
                                            // multiple
                                            onChange={(e) => handleDigitalAssetAdd(Array.from(e.target.files))}
                                            accept=".pdf,.zip,.mp3,.mp4,.doc,.docx,.ppt,.pptx,.txt,.jpg,.png"
                                            disabled={uploadingAssets || currentFileCount >= maxFiles}
                                            className="sr-only"
                                        />
                                        <label
                                            htmlFor="digital-files-edit"
                                            className={`cursor-pointer block ${(uploadingAssets || currentFileCount >= maxFiles) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                                                <Plus className="w-5 h-5 text-gray-400" />
                                            </div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {uploadingAssets ? 'Processing...' :
                                                    currentFileCount >= maxFiles ? 'File limit reached' :
                                                        'Choose files to upload'}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Max {maxFileSize / (1024 * 1024)}MB per file{maxFiles > 1 ? `, ${maxFiles} files total` : ''}
                                            </p>
                                            {/* ADD: Show total size usage */}
                                            {/* <p className="text-xs text-gray-500 mt-1">
                                                {formatFileSize(currentTotalSize)} / {formatFileSize(maxTotalSize)} used
                                            </p> */}
                                        </label>
                                    </div>

                                    {/* Show pending assets */}
                                    {pendingDigitalAssets.length > 0 && (
                                        <div className="space-y-2">
                                            <h4 className="text-sm font-medium text-gray-700">Files ready to upload:</h4>
                                            <div className="space-y-2 max-h-32 overflow-y-auto">
                                                {pendingDigitalAssets.map((asset) => (
                                                    <div key={asset.id} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                                                        <div className="flex items-center space-x-2 min-w-0">
                                                            <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                                                                <Package className="w-3 h-3 text-blue-600" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-medium text-gray-900 truncate">{asset.originalName}</p>
                                                                <p className="text-xs text-gray-500">{formatFileSize(asset.fileSize)}</p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemovePendingAsset(asset.id)}
                                                            className="flex-shrink-0 p-1 text-red-500 hover:text-red-700 transition-colors"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Links Section */}
                            {digitalAssetType === 'links' && (
                                <div className="space-y-3">
                                    {pendingDigitalLinks.length > 0 && (
                                        <div className="space-y-2">
                                            <h4 className="text-sm font-medium text-gray-700">New links to add:</h4>
                                            <div className="space-y-2 max-h-32 overflow-y-auto">
                                                {pendingDigitalLinks.map((link) => (
                                                    <div key={link.id} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                                                        <div className="flex items-center space-x-2 min-w-0">
                                                            <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                                                                <span className="text-xs">🔗</span>
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                                    {link.linkDescription || link.originalName}
                                                                </p>
                                                                <p className="text-xs text-gray-500 truncate">
                                                                    {link.linkType.replace('_', ' ')} • {link.linkUrl.substring(0, 25)}...
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => setPendingDigitalLinks(prev => prev.filter(l => l.id !== link.id))}
                                                            className="flex-shrink-0 p-1 text-red-500 hover:text-red-700 transition-colors"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <DigitalLinkForm
                                        onLinkAdd={(newLink) => {
                                            // ADD: Check limit before adding link
                                            if (currentFileCount >= maxFiles) {
                                                setErrorMessage(`You can only add ${maxFiles} digital items per product on the free plan.`);
                                                return;
                                            }
                                            setPendingDigitalLinks(prev => [...prev, {
                                                ...newLink,
                                                assetType: 'URL',
                                                id: Date.now() + Math.random()
                                            }]);
                                        }}
                                    />
                                </div>
                            )}

                            {/* Digital Settings */}
                            {(pendingDigitalAssets.length > 0 || pendingDigitalLinks.length > 0 || currentAssets.length > 0) && (
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <div className="flex items-center space-x-2 mb-3">
                                        <Settings className="h-4 w-4 text-gray-600" />
                                        <h4 className="text-sm font-medium text-gray-700">Access Settings</h4>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Usage Limit
                                            </label>
                                            <select
                                                value={digitalSettings.downloadLimit}
                                                onChange={(e) => handleDigitalSettingsChange('downloadLimit', parseInt(e.target.value))}
                                                className="w-full px-2 bg-white py-1.5 text-sm border border-gray-300 rounded-md focus:border-[#191970] focus:ring-1 focus:ring-[#191970]"
                                            >
                                                <option value={-1}>Unlimited</option>
                                                <option value={1}>1 download</option>
                                                <option value={3}>3 downloads</option>
                                                <option value={5}>5 downloads</option>
                                                <option value={10}>10 downloads</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Link Expiry
                                            </label>
                                            <select
                                                value={digitalSettings.linkExpiryHours}
                                                onChange={(e) => handleDigitalSettingsChange('linkExpiryHours', parseInt(e.target.value))}
                                                className="w-full bg-white px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:border-[#191970] focus:ring-1 focus:ring-[#191970]"
                                            >
                                                <option value={-1}>Never expires</option>
                                                <option value={24}>24 hours</option>
                                                <option value={48}>48 hours</option>
                                                <option value={168}>1 week</option>
                                                <option value={720}>1 month</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Product Image */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
                        <div className="space-y-3">
                            <input
                                type="file"
                                id="image"
                                onChange={handleImageChange}
                                accept="image/*"
                                disabled={isCompressing}
                                className="sr-only"
                            />

                            {previewUrl ? (
                                <div className="relative inline-block">
                                    <img src={previewUrl} alt="Preview" className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200" />
                                    <label
                                        htmlFor="image"
                                        className="absolute inset-0 bg-black bg-opacity-40 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                                    >
                                        <span className="text-white text-xs">Change</span>
                                    </label>
                                </div>
                            ) : (
                                <label
                                    htmlFor="image"
                                    className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors"
                                >
                                    <div className="flex flex-col items-center justify-center pt-2 pb-2">
                                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-1">
                                            <span className="text-gray-400 text-sm">📷</span>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            {isCompressing ? 'Processing...' : 'Add product image'}
                                        </p>
                                    </div>
                                </label>
                            )}

                            {errorMessage && (
                                <p className="text-xs text-red-600">{errorMessage}</p>
                            )}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            disabled={isUpdating}
                            className="w-full py-3 px-4 bg-[#191970] text-white text-sm font-medium rounded-lg hover:bg-[#191970]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#191970] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {isUpdating ? 'Updating Product...' : 'Update Product'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditItemPopup;